import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';

interface AuthToken {
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
}

interface AuthState {
  user: User | null;
  token: AuthToken | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

type AuthStore = AuthState & AuthActions;

// Demo user for testing
const demoUser: User = {
  id: 'demo-user-1',
  name: 'Demo User',
  email: 'demo@comfyui.dev',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=demo',
  role: 'USER',
  createdAt: new Date(),
  updatedAt: new Date(),
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: demoUser,
      token: {
        accessToken: 'demo-token',
        refreshToken: 'demo-refresh-token',
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
      },
      isAuthenticated: true,
      isLoading: false,
      error: null,

      // Actions
      login: async (_email: string, _password: string) => {
        set({ isLoading: true, error: null });
        
        // Demo login - always succeeds
        setTimeout(() => {
          set({
            user: demoUser,
            token: {
              accessToken: 'demo-token',
              refreshToken: 'demo-refresh-token',
              expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
            },
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        }, 1000);
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
      },

      updateProfile: async (data: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...data } });
        }
      },

      setUser: (user) => set({ user }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-store-demo',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);